// ShowFolderContentTreeView.js - Tree view component for displaying Autodesk Construction Cloud folder structure
import { useEffect, useState } from 'react';
// Global state provider for sharing data across components
import { useGlobalProvider } from '../contexts/GlobalProvider';
// API services for fetching folder content and file versions
import { fetchFolderContent } from '../api/foldersContentService';
import { getItemVersions } from '../api/versionsService';
// Accessible tree view component for hierarchical data display
import TreeView from 'react-accessible-treeview';
// Authentication context for API access tokens
import {useAuth} from "../contexts/AuthContext";
// Utility for displaying appropriate file type icons
import { getFileIcon } from '../utilities/fileTypeIcons';

/**
 * DirectoryTreeView Component - Displays Autodesk Construction Cloud folder structure as an interactive tree
 * 
 * This component renders a hierarchical tree view of folders and files from Autodesk Construction Cloud,
 * with support for expanding folders, viewing file versions, and handling user interactions.
 * Features include lazy loading, version expansion, and real-time refresh capabilities.
 */
const DirectoryTreeView = () => {
  // Extract global state and functions from the global provider
  const { selectedProjectId, accTopFolder, accCurrentNode, setAccCurrentNode, refreshTreeTrigger } = useGlobalProvider();

  // Get the top-level folder ID from the selected project
  const accFolderId = accTopFolder?.[0]?.id || null;

  // State for storing the tree structure data
  const [folderData, setFolderData] = useState([]);
  // Map for efficient node lookup and relationship management
  const [nodeMap, setNodeMap] = useState({});
  // Track which folders have been expanded to avoid redundant API calls
  const [expandedFolders, setExpandedFolders] = useState({});
  // Track which files have their versions expanded
  const [expandedVersions, setExpandedVersions] = useState({});
  // Cache for file version data to avoid repeated API calls
  const [versionData, setVersionData] = useState({});
  // Error state for displaying API or processing errors
  const [error, setError] = useState(null);
  // Loading state for user feedback during API operations
  const [isLoading, setIsLoading] = useState(false);
  // Authentication context for API access tokens and auth status
  const { getAccessToken, isAuthenticated, isFetchingToken } = useAuth();

  /**
   * Fetch folder content from Autodesk Construction Cloud API
   * Handles authentication, loading states, and error management
   * @param {string} folderId - The ID of the folder to fetch content for
   * @returns {Object|undefined} The fetched folder data or undefined on error
   */
  const fetchContent = async (folderId) => {
    console.log('fetchContent for folder:', folderId);
    try {
      const token = await getAccessToken();
      setIsLoading(true); // Show loading indicator
      setError(null); // Clear any previous errors
      const fetchedData = await fetchFolderContent({
        accessToken: token,
        selectedProjectId,
        accFolderId: folderId,
      });
      setIsLoading(false); // Hide loading indicator
      return fetchedData;
    } catch (err) {
      console.error('Error during fetchContent:', err);
      setError(err); // Store error for user display
      setIsLoading(false); // Hide loading indicator
    }
  };

  /**
   * Fetch all versions for a specific file item
   * Retrieves version history with metadata like modification dates and user info
   * @param {string} itemUrn - The URN of the item to fetch versions for
   * @returns {Array} Array of version objects or empty array on error
   */
  const fetchVersions = async (itemUrn) => {
    try {
      const token = await getAccessToken();
      
      const versionsResponse = await getItemVersions({
        selectedProjectId,
        itemUrn,
        accessToken: token,
      });
      
      console.log('Raw versions response:', versionsResponse);
      console.log('Versions response data:', versionsResponse?.data);
      
      if (versionsResponse && versionsResponse.data) {
        console.log('Number of versions found:', versionsResponse.data.length);
        // Log detailed version information for debugging
        versionsResponse.data.forEach((version, index) => {
          console.log(`Version ${index + 1}:`, version);
          console.log(`- ID: ${version.id}`);
          console.log(`- Type: ${version.type}`);
          console.log(`- Attributes:`, version.attributes);
          console.log(`- Relationships:`, version.relationships);
          console.log(`- Storage ID: ${version.relationships?.storage?.data?.id}`);
        });
        
        // Cache the version data to avoid repeated API calls
        setVersionData(prev => ({
          ...prev,
          [itemUrn]: versionsResponse.data
        }));
        return versionsResponse.data;
      }
      console.log('No version data found in response');
      return [];
    } catch (err) {
      console.error('Error fetching versions:', err);
      console.error('Error details:', err.message);
      return [];
    }
  };

  // Effect: Clear all tree data when project selection changes
  // This ensures we don't show stale data from the previous project
  useEffect(() => {
    setNodeMap({});
    setFolderData([]);
  }, [selectedProjectId]);

  // Effect: Handle tree refresh trigger from external components
  // Performs a complete tree refresh when triggered, clearing all cached data
  useEffect(() => {
    if (refreshTreeTrigger > 0 && accFolderId) {
      console.log('Refresh trigger detected, performing complete tree refresh...');
      
      // Force a complete refresh by clearing ALL cached data
      console.log('Clearing all cached tree data...');
      setNodeMap({});
      setFolderData([]);
      setExpandedFolders({});
      setExpandedVersions({});
      setVersionData({});
      setError(null);
      
      // Clear the current node selection to avoid stale references
      setAccCurrentNode('');
      
      // Add a small delay to ensure state is cleared, then fetch fresh data
      setTimeout(() => {
        console.log('Fetching fresh tree data from API...');
        fetchAndBuildTree(accFolderId, true); // Pass isRefresh = true
      }, 100);
    }
  }, [refreshTreeTrigger, accFolderId]);

  // Effect: Auto-load document tree when component mounts and prerequisites are met
  // This replaces the manual "Show document tree" button functionality
  useEffect(() => {
    if (isAuthenticated && !isFetchingToken && selectedProjectId && selectedProjectId.trim() !== '' && accFolderId && folderData.length === 0) {
      console.log('Auto-loading document tree on accordion open...');
      fetchAndBuildTree(accFolderId);
    }
  }, [isAuthenticated, isFetchingToken, selectedProjectId, accFolderId, folderData.length]);


  /**
   * Fetch folder content and build the tree structure
   * Validates prerequisites before making API calls and building the tree
   * @param {string} accFolderId - The folder ID to fetch content for
   * @param {boolean} isRefresh - Whether this is a refresh operation (clears cache)
   */
  const fetchAndBuildTree = async (accFolderId, isRefresh = false) => {
    console.log('fetchAndBuildTree accFolderId:', accFolderId, 'isRefresh:', isRefresh);
    // Validate all prerequisites before proceeding
    if (!isAuthenticated || isFetchingToken || !selectedProjectId || selectedProjectId.trim() === '' || !accFolderId) {
      console.warn('fetchAndBuildTree prerequisites not met');
      return;
    }
    const fetchedData = await fetchContent(accFolderId);
    if (fetchedData) {
      buildTreeStructure(fetchedData, isRefresh);
    }
  }



  /**
   * Build the hierarchical tree structure from fetched folder data
   * Creates nodes for folders, files, and versions with proper parent-child relationships
   * @param {Object} fetchedData - The raw data from the API containing folders and files
   * @param {boolean} isRefresh - Whether to start with a clean node map or preserve existing state
   */
  const buildTreeStructure = (fetchedData, isRefresh = false) => {
    if (!fetchedData || !Array.isArray(fetchedData.data)) return;

    // For refresh operations, start with a clean nodeMap to avoid stale data
    // Otherwise, preserve existing nodeMap to maintain expanded folder states
    let updatedNodeMap = isRefresh ? {} : { ...nodeMap };

    // Initialize nodeMap from folderData if it's empty and not a refresh
    if (!isRefresh && Object.keys(updatedNodeMap).length === 0 && folderData.length > 0) {
      folderData.forEach((node) => {
        updatedNodeMap[node.id] = {
          ...node,
          children: new Set(node.children),
        };
      });
    }

    /**
     * Helper function to create tree nodes from API data
     * Transforms raw API items into tree node objects with proper structure
     * @param {Object} item - Raw item data from the API
     * @param {string|null} parentId - ID of the parent node, null for root nodes
     * @returns {Object} Formatted tree node object
     */
    const createNode = (item, parentId) => {
      const transformedId = item.id;
      const isFile = item.type === 'items';

      const node = {
        id: transformedId,
        type: item.type,
        name: item.attributes.displayName || item.attributes.name,
        storage: item.relationships.tip?.data?.id,
        parent: parentId, // Can be null for root nodes
        children: updatedNodeMap[transformedId]?.children || new Set(),
        isBranch: item.type === 'folders' || (isFile && expandedVersions[transformedId]),
        isFile: isFile,
        hasVersions: false, // Will be updated when versions are checked
        itemUrn: isFile ? transformedId : null, // Store URN for version fetching
      };

      updatedNodeMap[transformedId] = node;
      return node;
    };

    /**
     * Helper function to create version nodes for files with multiple versions
     * Creates child nodes representing different versions of a file
     * @param {Object} version - Version data from the API
     * @param {string} parentId - ID of the parent file node
     * @param {number} versionNumber - Version number for display
     * @returns {Object} Formatted version node object
     */
    const createVersionNode = (version, parentId, versionNumber) => {
      const versionId = `${parentId}_v${versionNumber}`;

      const versionNode = {
        id: versionId,
        type: 'version',
        name: `v${versionNumber}`,
        storage: version.relationships?.storage?.data?.id,
        parent: parentId,
        folderUrn: updatedNodeMap[parentId]?.parent || null,
        children: [],
        isBranch: false,
        isFile: false,
        isVersion: true,
        versionData: version,
      };

      updatedNodeMap[versionId] = versionNode;
      return versionNode;
    };

    // Process all folders and items
    const fileNodes = []; // Keep track of file nodes to check for versions
    
    fetchedData.data.forEach((item) => {
      // Set parentId to null if there's no parent (root node)
      const parentId = item.relationships.parent?.data?.id || null;

      // Create or update the node
      const node = createNode(item, parentId);
      
      // Track file nodes for version checking
      if (node.isFile) {
        fileNodes.push(node);
      }

      // If the node has a parent, add it to the parent's children
      if (parentId !== null) {
        if (!updatedNodeMap[parentId]) {
          // If parent node doesn't exist yet, create it
          updatedNodeMap[parentId] = {
            id: parentId,
            name: 'Unknown',
            parent: null,
            children: new Set(),
            isBranch: true,
          };
        }

        updatedNodeMap[parentId].children.add(node.id);
      }
    });

    // Check for multiple versions for file nodes (async operation)
    // We'll do this in the background and update the UI when ready
    fileNodes.forEach(async (fileNode) => {
      try {
        const versions = await fetchVersions(fileNode.itemUrn);
        if (versions && versions.length > 1) {
          // Update the node to indicate it has multiple versions
          setNodeMap(prevNodeMap => {
            const updated = { ...prevNodeMap };
            if (updated[fileNode.id]) {
              updated[fileNode.id].hasVersions = true;
            }
            return updated;
          });
          
          // Update the tree data to reflect the change
          setFolderData(prevData =>
            prevData.map(node =>
              node.id === fileNode.id
                ? { ...node, hasVersions: true }
                : node
            )
          );
        }
      } catch (error) {
        console.error('Error checking versions for file:', fileNode.id, error);
      }
    });

    // Convert nodeMap to an array and convert Sets back to arrays
    const treeData = Object.values(updatedNodeMap).map((node) => ({
      ...node,
      children: Array.from(node.children),
    }));

    setNodeMap(updatedNodeMap);
    setFolderData(treeData);
    // console.log('treedata', folderData);
  };

  /**
   * Handle user clicks on tree nodes
   * Manages folder expansion, file version expansion, and node selection
   * @param {Object} node - The clicked tree node object
   */
  const handleNodeClick = async (node) => {
    console.log('Node clicked:', node);
    setAccCurrentNode(node);
    
    if (node.type === 'folders' && !expandedFolders[node.id]) {
      // Handle folder expansion - fetch and display folder contents
      setExpandedFolders((prev) => ({ ...prev, [node.id]: true }));

      // Fetch the content of the clicked folder
      const fetchedData = await fetchContent(node.id);
      if (fetchedData) {
        buildTreeStructure(fetchedData);
      }
    } else if (node.isFile && !expandedVersions[node.id]) {
      // Handle file version expansion - show all versions of the file
      setExpandedVersions((prev) => ({ ...prev, [node.id]: true }));
      
      // Fetch versions for this file
      const versions = await fetchVersions(node.itemUrn);
      if (versions && versions.length > 1) {
        // Update the node to show it has versions and rebuild tree
        const updatedNodeMap = { ...nodeMap };
        if (updatedNodeMap[node.id]) {
          updatedNodeMap[node.id].hasVersions = true;
          updatedNodeMap[node.id].isBranch = true;
          
          // Add version nodes as children with formatted display names
          versions.forEach((version, index) => {
            const versionNumber = versions.length - index; // Latest version first
            const storageId = version.relationships?.storage?.data?.id;
            
            // Format the date and user information for display
            let displayName = `v${versionNumber}`;
            
            if (version.attributes) {
              const lastModifiedTime = version.attributes.lastModifiedTime;
              const lastModifiedUserName = version.attributes.lastModifiedUserName;
              
              if (lastModifiedTime) {
                // Parse and format the date in UK format
                const date = new Date(lastModifiedTime);
                const formattedDate = date.toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                });
                const formattedTime = date.toLocaleTimeString('en-GB', {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false
                });
                
                displayName += ` (${formattedDate} ${formattedTime}`;
                
                if (lastModifiedUserName) {
                  displayName += ` - ${lastModifiedUserName}`;
                }
                
                displayName += ')';
              }
            }
            
            // Create version node with formatted display name
            const versionNode = {
              id: `${node.id}_v${versionNumber}`,
              type: 'version',
              name: displayName,
              storage: storageId,
              parent: node.id,
              children: [],
              isBranch: false,
              isFile: false,
              isVersion: true,
              versionData: version,
            };
            
            updatedNodeMap[versionNode.id] = versionNode;
            updatedNodeMap[node.id].children.add(versionNode.id);
          });
        }
        
        // Convert nodeMap to tree data
        const treeData = Object.values(updatedNodeMap).map((n) => ({
          ...n,
          children: Array.from(n.children),
        }));
        
        setNodeMap(updatedNodeMap);
        setFolderData(treeData);
      }
    }
  };

  return (
      <div>
        {/* Loading indicator */}
        {isLoading && <p>Loading...</p>}
        
        {/* Error display */}
        {error && <p>Error: {error.message}</p>}

        <div>
          {/* Render the tree view only when data is available */}
          {folderData.length > 0 && (
              <div className="directory">
                <TreeView
                    data={folderData}
                    aria-label="directory tree"
                    nodeRenderer={({ element, isBranch, isExpanded, isSelected, getNodeProps, level, handleExpand, handleSelect }) => (
                        <div
                            {...getNodeProps({
                              onClick: (event) => {
                                // Handle node click with custom logic, then delegate to tree view handlers
                                handleNodeClick(element);
                                handleSelect(event);
                                handleExpand(event);
                              },
                            })}
                            // Apply different CSS classes based on selection state
                            className={isSelected ? "item-selected" : "item-normal"}
                            // Indent based on tree level for visual hierarchy
                            style={{ paddingLeft: 20 * (level - 1) }}
                        >
                          {/* Display appropriate icon based on node type and state */}
                          {getFileIcon(
                            element.name,
                            element.type === 'folders',
                            isExpanded,
                            element.hasVersions,
                            element.isVersion
                          )} {element.name}
                        </div>
                    )}
                />
              </div>
          )}
        </div>
      </div>
  );
};

export default DirectoryTreeView;
